import {
  getInvoiceStatusDisplay,
  getProcessingStatusDisplay,
  getReviewStatusDisplay,
} from "@/lib/invoiceStatus";
import { cn } from "@/lib/utils";

function isBrightStatusColor(color) {
  return color?.toLowerCase() === "#f1ee16";
}

const DISPLAY = {
  auto: getInvoiceStatusDisplay,
  review: getReviewStatusDisplay,
  processing: getProcessingStatusDisplay,
};

/**
 * A status badge.
 *
 * `kind` picks which status it shows. The queues give review and processing a
 * column each, so they ask for one explicitly; the detail header has room for
 * only one badge and uses `auto`, which shows whichever status is the live one.
 *
 * The two keep distinct silhouettes — review is a filled tint, processing is a
 * dot beside its label — so that side by side in adjacent columns the reader
 * can tell which is which without reading the header.
 */
export function InvoiceStatusBadge({ invoice, kind = "auto", compact = false, nativeTitle = true }) {
  const status = (DISPLAY[kind] ?? getInvoiceStatusDisplay)(invoice);

  const isReviewLook =
    kind === "review" || (kind === "auto" && invoice?.processingStatusId === 2);
  const bright = isBrightStatusColor(status.color);

  return (
    <span
      role="status"
      title={nativeTitle ? status.tooltip : undefined}
      aria-label={status.tooltip ?? status.label}
      data-bright={bright ? "true" : undefined}
      className={cn(
        "invoice-status-badge",
        isReviewLook ? "invoice-status-badge--review" : "invoice-status-badge--processing",
        compact && "invoice-status-badge-compact",
      )}
      style={{ "--status-color": status.color }}
    >
      {!isReviewLook && <span className="invoice-status-dot" aria-hidden="true" />}
      <span className="invoice-status-label">{status.label}</span>
    </span>
  );
}
